import { useState, useEffect, useMemo } from "react";
import { DollarSign, UserCheck, Clock, TrendingDown } from "lucide-react";

const usePayrollFilters = ({ t, currentTheme, payrollData }) => {
  const [visibleItems, setVisibleItems] = useState(10);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [selectedMonth, setSelectedMonth] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [selectedLaborMode, setSelectedLaborMode] = useState("All");

  const [statusSearch, setStatusSearch] = useState(() =>
    t("allStatuses")
  );
  const [departmentSearch, setDepartmentSearch] = useState(() =>
    t("allDepartments")
  );
  const [companySearch, setCompanySearch] = useState(() =>
    t("allCompanies")
  );
  const [laborModeSearch, setLaborModeSearch] = useState(() =>
    t("allLaborModes")
  );
  const [yearSearch, setYearSearch] = useState(
    new Date().getFullYear().toString()
  );
  const [monthSearch, setMonthSearch] = useState(() =>
    t(
      [
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december",
      ][new Date().getMonth()]
    )
  );

  // Reset visible items when any filter changes
  useEffect(() => {
    setVisibleItems(10);
  }, [searchTerm, statusFilter, selectedDepartment, selectedCompany, selectedLaborMode, selectedMonth]);

  const filteredPayrollData = useMemo(() => {
    return (payrollData || []).filter((payroll) => {
      const matchesSearch =
        payroll.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payroll.employeeId?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "All" || payroll.status === statusFilter;

      const matchesDepartment =
        selectedDepartment === "All" ||
        payroll.department === selectedDepartment;

      const matchesCompany =
        selectedCompany === "All" ||
        (payroll.workingCompany || "") === selectedCompany;

      const matchesLaborMode =
        selectedLaborMode === "All" ||
        (payroll.laborMode || "") === selectedLaborMode;

      const fallbackDate = payroll.paidDate || payroll.payrollStartDate || null;
      const rowYear = Number(
        payroll.year || (fallbackDate ? new Date(fallbackDate).getFullYear() : 0)
      );
      const rowMonth = Number(
        payroll.month ||
          (fallbackDate ? new Date(fallbackDate).getMonth() + 1 : 0)
      );

      const selectedYear = Number(selectedMonth?.year || 0);
      const selectedMonthValue = Number(selectedMonth?.month || 0);

      const matchesYear = !selectedYear || !rowYear || rowYear === selectedYear;
      const matchesMonth =
        !selectedMonthValue || !rowMonth || rowMonth === selectedMonthValue;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesDepartment &&
        matchesCompany &&
        matchesLaborMode &&
        matchesYear &&
        matchesMonth
      );
    });
  }, [payrollData, searchTerm, statusFilter, selectedDepartment, selectedCompany, selectedLaborMode, selectedMonth]);

  const totalGrossSalary = useMemo(
    () => filteredPayrollData.reduce((sum, emp) => sum + (emp.grossSalary || 0), 0),
    [filteredPayrollData]
  );
  const totalNetSalary = useMemo(
    () => filteredPayrollData.reduce((sum, emp) => sum + (emp.netSalary || 0), 0),
    [filteredPayrollData]
  );
  const totalDeductions = useMemo(
    () => filteredPayrollData.reduce((sum, emp) => sum + (emp.totalDeductions || 0), 0),
    [filteredPayrollData]
  );
  const averageKPI = useMemo(
    () =>
      filteredPayrollData.length > 0
        ? filteredPayrollData.reduce((sum, emp) => sum + (emp.kpiScore || 0), 0) /
          filteredPayrollData.length
        : 0,
    [filteredPayrollData]
  );

  const salaryStats = useMemo(
    () => [
      {
        icon: DollarSign,
        value: `${totalGrossSalary.toLocaleString()} ${t("currency")}`,
        label: t("totalGross"),
        color: "#0004f1c4",
        subtitle: t("monthlyGross"),
        textColor: currentTheme === "dark" ? "#fff" : "#1a1a1a",
      },
      {
        icon: UserCheck,
        value: (payrollData || [])
          .filter((p) => p.status === "paid")
          .length.toString(),
        label: t("employeesPaid"),
        color: currentTheme.colors?.success,
        subtitle: t("currentMonth"),
      },
      {
        icon: Clock,
        value: (payrollData || [])
          .filter((p) => p.status === "pending")
          .length.toString(),
        label: t("pendingPayments"),
        color: currentTheme.colors?.warning,
        subtitle: t("awaitingApproval"),
      },
      {
        icon: TrendingDown,
        value: `${totalDeductions.toLocaleString()} ${t("currency")}`,
        label: t("averageKPI"),
        color: currentTheme.colors?.danger,
        subtitle: t("performanceIndicator"),
      },
    ],
    [payrollData, totalGrossSalary, totalDeductions, currentTheme, t]
  );

  return {
    employeeCount,
    setEmployeeCount,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    selectedMonth,
    setSelectedMonth,
    selectedDepartment,
    setSelectedDepartment,
    selectedCompany,
    setSelectedCompany,
    selectedLaborMode,
    setSelectedLaborMode,
    statusSearch,
    setStatusSearch,
    departmentSearch,
    setDepartmentSearch,
    companySearch,
    setCompanySearch,
    laborModeSearch,
    setLaborModeSearch,
    yearSearch,
    setYearSearch,
    monthSearch,
    setMonthSearch,
    visibleItems,
    setVisibleItems,
    filteredPayrollData,
    totalGrossSalary,
    totalNetSalary,
    totalDeductions,
    averageKPI,
    salaryStats,
  };
};

export default usePayrollFilters;